import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Field,
  Heading,
  Label,
  Link,
  Paragraph,
  Select,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type {
  HrOverviewTeam,
  HrWorkEnvironmentOverview,
} from '../../../../api/hrWorkEnvironment';
import type { HrSafetyReps } from '../../../../api/hrSafetyReps';
import { Routes } from '../../../../enums/Routes';
import {
  useHrSafetyRepsQuery,
  useRegisterSafetyRepMutation,
} from '../../../../hooks/queries/hr/useHrSafetyRepsQuery';
import { leadersText, teamsWithoutSafetyRep } from '../Overview/hrOverviewView';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import classes from '../Overview/Overview.module.css';

type Saved = { email: string; team: string };

const TeamRow = ({
  team,
  representatives,
  onSaved,
}: {
  team: HrOverviewTeam;
  representatives: HrSafetyReps['representatives'];
  onSaved: (saved: Saved) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [missing, setMissing] = useState(false);
  const register = useRegisterSafetyRepMutation();
  const id = `safety-rep-${team.id}`;
  const save = () => {
    const chosen = representatives.find((rep) => String(rep.userId) === userId);
    if (!chosen) {
      setMissing(true);
      return;
    }
    register.mutate(
      { userId: chosen.userId, teamIds: [team.id] },
      { onSuccess: () => onSaved({ email: chosen.email, team: team.name }) },
    );
  };
  return (
    <li className={classes.listRow}>
      <div className={classes.rowText}>
        <span className="ds-label">{team.name}</span>
        <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
          {t('hrOverview.safetyReps.team', {
            leaders: leadersText(team, t),
            count: team.size,
          })}
        </span>
      </div>
      <div className={classes.rowActions}>
        <Field data-size="sm" className={classes.select}>
          <Label htmlFor={id}>
            {t('hrOverview.safetyReps.label', { team: team.name })}
          </Label>
          <Select
            id={id}
            value={userId}
            aria-invalid={missing || undefined}
            onChange={(event) => {
              setUserId(event.target.value);
              setMissing(false);
            }}
          >
            <Select.Option value="">
              {t('hrOverview.safetyReps.choose')}
            </Select.Option>
            {representatives.map((rep) => (
              <Select.Option key={rep.userId} value={String(rep.userId)}>
                {rep.email}
              </Select.Option>
            ))}
          </Select>
          {missing && (
            <ValidationMessage>
              {t('hrOverview.safetyReps.missing')}
            </ValidationMessage>
          )}
          {register.isError && (
            <ValidationMessage>
              {t('hrOverview.safetyReps.failed')}
            </ValidationMessage>
          )}
        </Field>
        <Button
          variant="secondary"
          data-size="sm"
          loading={register.isPending}
          onClick={save}
        >
          {t('hrOverview.safetyReps.save')}
        </Button>
      </div>
    </li>
  );
};

const MissingSafetyReps = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t } = useTranslation();
  const { data } = useHrSafetyRepsQuery();
  const [saved, setSaved] = useState<Saved | null>(null);
  const teams = teamsWithoutSafetyRep(overview.teams);
  return (
    <>
      {saved && (
        <Alert data-color="success" role="status">
          <Paragraph data-size="sm">
            {t('hrOverview.safetyReps.saved', saved)}
          </Paragraph>
        </Alert>
      )}
      <section aria-labelledby="missing-heading" className={classes.card}>
        <Heading level={2} data-size="xs" id="missing-heading">
          {t('hrOverview.safetyReps.title', { count: teams.length })}
        </Heading>
        {teams.length === 0 ? (
          <Paragraph>{t('hrOverview.fix.safetyReps.ok')}</Paragraph>
        ) : (
          <ul className={classes.rows}>
            {teams.map((team) => (
              <TeamRow
                key={team.id}
                team={team}
                representatives={data?.representatives ?? []}
                onSaved={setSaved}
              />
            ))}
          </ul>
        )}
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('hrOverview.safetyReps.note')}{' '}
          <Link asChild>
            <RouterLink to={Routes.HRSafetyRepsFullPath}>
              {t('hrOverview.safetyReps.noteLink')}
            </RouterLink>
          </Link>
        </Paragraph>
      </section>
    </>
  );
};

export const HRMissingSafetyReps = (): ReactElement => (
  <OverviewSubpage page="safetyReps">
    {(overview) => <MissingSafetyReps overview={overview} />}
  </OverviewSubpage>
);
